import React, { useEffect, useState } from 'react';
import { Gantt } from '../components/Gantt.js';
import {Map} from '../components/Map.js'
import { Col, Row, Container } from 'react-bootstrap';
import './ProjectPage.css'
import { HashLink } from 'react-router-hash-link';

function ProjectPage({ endpoint }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ganttData, setGanttData] = useState([]);
  const hasValue = (value) =>
    value !== null &&
    value !== undefined &&
    String(value).trim() !== '';

  const formatAdmissionDate = (value) => {
    if (!hasValue(value)) return null;
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Fetch data from endpoint
  useEffect(() => {
    fetch(endpoint)
      .then((response) => response.json())
      .then((data) => {
        setData(data);
        setGanttData(data.tasks || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error('[FETCH] Error fetching data:', error);
        setLoading(false);
      });
  }, [endpoint]);

  // Helper function to validate coordinates
  const hasValidCoordinates = (lat, long) => {
    return (
      typeof lat === 'number' && 
      typeof long === 'number' && 
      !isNaN(lat) && 
      !isNaN(long) &&
      lat !== null &&
      long !== null
    );
  };

  // Parse coordinates with validation
  const lat = data.latitude ? parseFloat(data.latitude) : null;
  const long = data.longitude ? parseFloat(data.longitude) : null;
  const shouldShowMap = hasValidCoordinates(lat, long);

  const primaryContact = data?.contact?.[0] ?? {};
  const websiteUrl = hasValue(data?.website) ? data.website.split(',')[0].trim() : null;
  const contactItems = [
    hasValue(data?.sponsor)
      ? { label: 'Sponsor:', value: data.sponsor }
      : null,
    hasValue(primaryContact?.name)
      ? { label: 'Name:', value: primaryContact.name }
      : null,
    hasValue(primaryContact?.title)
      ? { label: 'Title:', value: primaryContact.title }
      : null,
    hasValue(primaryContact?.email)
      ? {
          label: 'Email:',
          value: (
            <a href={`mailto:${primaryContact.email}`} className="website-link">
              {primaryContact.email}
            </a>
          )
        }
      : null,
    hasValue(websiteUrl)
      ? {
          label: 'Website:',
          value: (
            <a
              href={websiteUrl}
              className="website-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {websiteUrl}
            </a>
          )
        }
      : null
  ].filter(Boolean);

  const hasTasks = (ganttData?.length ?? 0) > 0;

  // Dynamically set the page title based on the project name
  useEffect(() => {
    if (data && data.project) {
      document.title = `${data.project} | Office of Transformation & Opportunity`;
    } else {
      document.title = 'PA Permit Fast Track Program | Office of Transformation & Opportunity';
    }
  }, [data.project]);

  if (loading) {
    return <div>Loading...</div>; // Show a loading indicator while fetching data
  }

  return (
    <Container className="container">
      <Row className="header">
        <Col>
          {/* <div className="sponsor-label">{data.sponsor}</div> */}
          <h1 className="project-title">{data.project}</h1>
          {(() => {
            const hasProjectType = hasValue(data?.type);
            const hasCounty = hasValue(data?.county);
            if (!hasProjectType && !hasCounty) return null;
            let text;
            if (hasProjectType && hasCounty) {
              text = `${data.type} in ${data.county} County`;
            } else if (hasCounty) {
              text = `${data.county} County`;
            } else {
              text = data.type;
            }
            return <p className="project-meta">{text}</p>;
          })()}
          {hasValue(data?.admissionDate) && (
            <div className="project-admission-date">
              Fast Track Acceptance:{' '}
              <span className="project-field-value">
                {formatAdmissionDate(data.admissionDate)}
              </span>
            </div>
          )}
          <div className="project-status">
            Status:{' '}
            <span className="project-field-value">{data?.status || "N/A"}</span>
          </div>
          <p className="desc-text">{data.description}</p>
        </Col>
      </Row>

      <Row className="info">
        <Col md={8}>
          {shouldShowMap ? (
            <Map className="map" latitude={lat} longitude={long} />
          ) : (
            <div className="no-map-message">
              <p>Location data not available for this project</p>
            </div>
          )}
        </Col>
        <Col className="contact-box" md={4}>
          <h2 className="contact-title">Point of Contact</h2>
          {contactItems.map((item) => (
            <div className="contact-item-inline" key={item.label}>
              <span className="contact-label">{item.label}</span>
              <span className="contact-value">{item.value}</span>
            </div>
          ))}
        </Col>
      </Row>

      <Row className="timeline">
        <Col>
          <h2 className="timeline-header">Timeline</h2>
          {hasTasks ? (
            <>
              <Gantt data={ganttData}/>
              <HashLink to="/agencies/oto/fasttrack#permit-glossary">See Permit Glossary and Definitions</HashLink>
            </>
          ) : (
            <div className="updates-box">
              <p>Awaiting permit timeline information.</p>
            </div>
          )}
        </Col>
      </Row>

      <Row>
        <Col>
          <h2 className="project-updates-header">Project Notes</h2>
          <div className="updates-box">
            {data.notes && data.notes.length > 0 ? (
              <ul>
                {data.notes.map((note, index) => (
                  <li key={index}>{note}</li>
                ))}
              </ul>
            ) : (
              <p>There are no project notes at this time.</p>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  )
}

export default ProjectPage;